import React, { useEffect, useRef, useState } from 'react';
import { Edit, Loader2, Play, Plus, Trash2, Upload } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import BaseLayout from '@/shared/components/layout/BaseLayout';
import { VideoPlayerModal } from '@/shared/components/VideoPlayerModal';
import { useUser } from '@/shared/hooks/useUser';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { VerticalTab } from '@/shared/ui/custom/VerticalTab';
import { Textarea } from '@/shared/ui/textarea';
import { supportApi } from '../api/api';
import { BugReportForm } from '../forms/BugReportForm';
import { BugReportSuccess } from '../components/BugReportSuccess';
import { BugReportList } from '../components/BugReportList';
import type { SupportVideoGuide } from '../types/types';

const SUPPORT_TABS = new Set(['reports', 'guides']);

type GuideFormState = {
  title: string;
  feature: string;
  description: string;
  sort_order: string;
  video: File | null;
};

const emptyGuideForm: GuideFormState = {
  title: '',
  feature: '',
  description: '',
  sort_order: '0',
  video: null,
};

const VideoGuideForm: React.FC<{
  initialGuide?: SupportVideoGuide | null;
  uploadProgress: number;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (form: GuideFormState) => void;
}> = ({ initialGuide, uploadProgress, isSaving, onCancel, onSubmit }) => {
  const [form, setForm] = useState<GuideFormState>(emptyGuideForm);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const captureInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!initialGuide) {
      setForm(emptyGuideForm);
      return;
    }
    setForm({
      title: initialGuide.title,
      feature: initialGuide.feature,
      description: initialGuide.description,
      sort_order: String(initialGuide.sort_order ?? 0),
      video: null,
    });
  }, [initialGuide]);

  useEffect(() => {
    if (!form.video) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(form.video);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.video]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim() || (!initialGuide && !form.video)) return;
    onSubmit(form);
  };

  const handleVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, video: event.target.files?.[0] ?? null }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="guide-title">Title</Label>
        <Input
          id="guide-title"
          value={form.title}
          onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
          placeholder="Creating a punch list item"
          autoFocus
          required
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
        <div className="space-y-2">
          <Label htmlFor="guide-feature">Feature</Label>
          <Input
            id="guide-feature"
            value={form.feature}
            onChange={(event) => setForm((prev) => ({ ...prev, feature: event.target.value }))}
            placeholder="Punch Lists"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="guide-sort-order">Order</Label>
          <Input
            id="guide-sort-order"
            type="number"
            min="0"
            value={form.sort_order}
            onChange={(event) => setForm((prev) => ({ ...prev, sort_order: event.target.value }))}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="guide-description">Description</Label>
        <Textarea
          id="guide-description"
          className="min-h-[90px] resize-none"
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
          placeholder="A short note about what this guide covers."
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="guide-video">Video File</Label>
        <input
          ref={fileInputRef}
          id="guide-video"
          type="file"
          className="hidden"
          accept="video/*"
          onChange={handleVideoChange}
          disabled={isSaving}
        />
        <input
          ref={captureInputRef}
          id="guide-video-capture"
          type="file"
          className="hidden"
          accept="video/*"
          capture="environment"
          onChange={handleVideoChange}
          disabled={isSaving}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSaving}
          >
            <Upload className="mr-2 h-4 w-4" />
            Choose Video
          </Button>
          <Button
            type="button"
            variant="outline"
            className="md:hidden"
            onClick={() => captureInputRef.current?.click()}
            disabled={isSaving}
          >
            Take Video
          </Button>
        </div>
        {form.video && (
          <p className="text-xs text-muted-foreground">
            Selected file: <span className="font-medium text-foreground">{form.video.name}</span>
          </p>
        )}
        {initialGuide && (
          <p className="text-xs text-muted-foreground">
            Optional. Upload a new file only if you want to replace the current video.
          </p>
        )}
        {previewUrl && !isSaving && (
          <video src={previewUrl} controls className="mt-2 max-h-48 w-full rounded-md border" />
        )}
      </div>
      {isSaving && uploadProgress > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Uploading video...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSaving || !form.title.trim() || (!initialGuide && !form.video)}
        >
          {isSaving ? 'Saving...' : initialGuide ? 'Save Guide' : 'Upload Guide'}
        </Button>
      </div>
    </form>
  );
};

const SupportPage: React.FC = () => {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') ?? 'guides';
  const activeTab = SUPPORT_TABS.has(tabParam) ? tabParam : 'guides';
  const [submitted, setSubmitted] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [guides, setGuides] = useState<SupportVideoGuide[]>([]);
  const [isLoadingGuides, setIsLoadingGuides] = useState(true);
  const [guideError, setGuideError] = useState<string | null>(null);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [editingGuide, setEditingGuide] = useState<SupportVideoGuide | null>(null);
  const [isSavingGuide, setIsSavingGuide] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [playingGuide, setPlayingGuide] = useState<
    (SupportVideoGuide & { playback_url?: string | null }) | null
  >(null);
  const [loadingPlaybackId, setLoadingPlaybackId] = useState<string | null>(null);
  const canManageGuides = Boolean(user?.is_client_admin || user?.is_staff || user?.is_superuser);

  const handleTabChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value === 'guides') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', value);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleSuccess = () => {
    setSubmitted(true);
    setListKey((k) => k + 1);
  };

  const loadGuides = async () => {
    try {
      setGuideError(null);
      setIsLoadingGuides(true);
      setGuides(await supportApi.getVideoGuides());
    } catch {
      setGuideError('Failed to load video guides.');
    } finally {
      setIsLoadingGuides(false);
    }
  };

  useEffect(() => {
    void loadGuides();
  }, []);

  const handleGuideSubmit = async (form: GuideFormState) => {
    try {
      setIsSavingGuide(true);
      setUploadProgress(0);
      const payload = {
        title: form.title.trim(),
        feature: form.feature.trim(),
        description: form.description.trim(),
        sort_order: Number(form.sort_order || 0),
      };
      if (editingGuide) {
        await supportApi.updateVideoGuide(
          editingGuide.id,
          { ...payload, video: form.video },
          setUploadProgress
        );
      } else if (form.video) {
        await supportApi.createVideoGuide(
          {
            ...payload,
            video: form.video,
            is_active: true,
          },
          setUploadProgress
        );
      }
      setIsGuideDialogOpen(false);
      setEditingGuide(null);
      await loadGuides();
    } catch {
      setGuideError('Failed to save video guide.');
    } finally {
      setIsSavingGuide(false);
      setUploadProgress(0);
    }
  };

  const handlePlayGuide = async (guide: SupportVideoGuide) => {
    try {
      setLoadingPlaybackId(guide.id);
      const playbackUrl = await supportApi.getVideoGuidePlaybackUrl(guide.id);
      setPlayingGuide({ ...guide, playback_url: playbackUrl });
    } catch {
      setGuideError('Failed to load video playback.');
    } finally {
      setLoadingPlaybackId(null);
    }
  };

  const handleDeleteGuide = async (guide: SupportVideoGuide) => {
    const confirmed = window.confirm(`Delete "${guide.title}"?`);
    if (!confirmed) return;
    try {
      await supportApi.deleteVideoGuide(guide.id);
      await loadGuides();
    } catch {
      setGuideError('Failed to delete video guide.');
    }
  };

  const reportBugContent = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Submit a Bug Report</h2>
        {submitted ? (
          <BugReportSuccess onReset={() => setSubmitted(false)} />
        ) : (
          <BugReportForm onSuccess={handleSuccess} />
        )}
      </div>

      <div>
        <h2 className="text-base font-semibold text-foreground mb-4">Your Submitted Reports</h2>
        <BugReportList key={listKey} />
      </div>
    </div>
  );

  const videoGuidesContent = (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">Video Guides</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Short walkthroughs for Ironview 360 features.
          </p>
        </div>
        {canManageGuides && (
          <Button
            onClick={() => {
              setEditingGuide(null);
              setIsGuideDialogOpen(true);
            }}
            className="w-full gap-2 sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            Upload Guide
          </Button>
        )}
      </div>

      {guideError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {guideError}
        </div>
      )}

      {isLoadingGuides ? (
        <div className="flex h-52 items-center justify-center rounded-xl border">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : guides.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-16 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Play className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">No video guides yet</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Admins can upload walkthroughs that everyone can watch here.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {guides.map((guide) => (
            <div
              key={guide.id}
              className="flex min-h-[190px] flex-col rounded-xl border bg-card p-4 shadow-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <h3 className="line-clamp-2 text-sm font-semibold text-foreground">
                    {guide.title}
                  </h3>
                  {guide.feature && (
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {guide.feature}
                    </Badge>
                  )}
                </div>
                {guide.description && (
                  <p className="line-clamp-4 text-sm text-muted-foreground">{guide.description}</p>
                )}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Button
                  size="sm"
                  className="flex-1 gap-2"
                  onClick={() => handlePlayGuide(guide)}
                  disabled={loadingPlaybackId === guide.id}
                >
                  {loadingPlaybackId === guide.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  Play
                </Button>
                {canManageGuides && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 w-9 p-0"
                      title="Edit guide"
                      onClick={() => {
                        setEditingGuide(guide);
                        setIsGuideDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 w-9 p-0 text-destructive hover:text-destructive"
                      title="Delete guide"
                      onClick={() => handleDeleteGuide(guide)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Support</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Encountered a bug? Let us know and we'll get it sorted out.
            </p>
          </div>

          <VerticalTab
            value={activeTab}
            onValueChange={handleTabChange}
            items={[
              { value: 'guides', label: 'Video Guides', content: videoGuidesContent },
              { value: 'reports', label: 'Report a Bug', content: reportBugContent },
            ]}
          />
        </div>
      </div>

      {canManageGuides && (
        <Dialog
          open={isGuideDialogOpen}
          onOpenChange={(open) => {
            setIsGuideDialogOpen(open);
            if (!open) setEditingGuide(null);
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingGuide ? 'Edit Video Guide' : 'Upload Video Guide'}</DialogTitle>
              <DialogDescription>
                Videos upload directly to the Ironview 360 media bucket.
              </DialogDescription>
            </DialogHeader>
            <VideoGuideForm
              initialGuide={editingGuide}
              uploadProgress={uploadProgress}
              isSaving={isSavingGuide}
              onCancel={() => {
                setIsGuideDialogOpen(false);
                setEditingGuide(null);
              }}
              onSubmit={handleGuideSubmit}
            />
          </DialogContent>
        </Dialog>
      )}

      {playingGuide && (
        <VideoPlayerModal
          title={playingGuide.title}
          description={playingGuide.description}
          videoUrl={playingGuide.playback_url}
          onClose={() => setPlayingGuide(null)}
        />
      )}
    </BaseLayout>
  );
};

export { SupportPage };
